import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { format, subMonths } from "date-fns"
import { useEffect } from "react"

import { ByPersonChart } from "@/components/reports/by-person-chart"
import { CashFlowChart } from "@/components/reports/cash-flow-chart"
import { CategoryBreakdownChart } from "@/components/reports/category-breakdown-chart"
import { MonthlyOverviewChart } from "@/components/reports/monthly-overview-chart"
import { ReportFilters } from "@/components/reports/report-filters"
import { ReportTransactionsTable } from "@/components/reports/report-transactions-table"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  getByPerson,
  getCashFlow,
  getCategoryBreakdown,
  getMonthlyOverview,
  listReportTransactions,
} from "@/http/finance"
import { useOrganization } from "@/store/organization"

type TReportsSearch = {
  startDate?: string
  endDate?: string
  paidOnly?: boolean
}

const getDefaultDates = () => {
  const now = new Date()
  return {
    startDate: format(subMonths(now, 3), "yyyy-MM-dd"),
    endDate: format(now, "yyyy-MM-dd"),
  }
}

function monthlyOverviewQueryOptions(
  orgSlug: string,
  startDate: string,
  endDate: string,
  paidOnly: boolean,
) {
  return queryOptions({
    queryKey: [
      "report-monthly-overview",
      orgSlug,
      startDate,
      endDate,
      paidOnly,
    ],
    queryFn: async () => {
      const response = await getMonthlyOverview({
        organizationSlug: orgSlug,
        startDate,
        endDate,
        paidOnly,
      })
      if (response.error) throw new Error(response.error.message)
      return response.data
    },
  })
}

function cashFlowQueryOptions(
  orgSlug: string,
  startDate: string,
  endDate: string,
  paidOnly: boolean,
) {
  return queryOptions({
    queryKey: ["report-cash-flow", orgSlug, startDate, endDate, paidOnly],
    queryFn: async () => {
      const response = await getCashFlow({
        organizationSlug: orgSlug,
        startDate,
        endDate,
        paidOnly,
      })
      if (response.error) throw new Error(response.error.message)
      return response.data
    },
  })
}

function categoryBreakdownQueryOptions(
  orgSlug: string,
  startDate: string,
  endDate: string,
  paidOnly: boolean,
) {
  return queryOptions({
    queryKey: [
      "report-category-breakdown",
      orgSlug,
      startDate,
      endDate,
      paidOnly,
    ],
    queryFn: async () => {
      const response = await getCategoryBreakdown({
        organizationSlug: orgSlug,
        startDate,
        endDate,
        paidOnly,
      })
      if (response.error) throw new Error(response.error.message)
      return response.data
    },
  })
}

function byPersonQueryOptions(
  orgSlug: string,
  startDate: string,
  endDate: string,
  paidOnly: boolean,
) {
  return queryOptions({
    queryKey: ["report-by-person", orgSlug, startDate, endDate, paidOnly],
    queryFn: async () => {
      const response = await getByPerson({
        organizationSlug: orgSlug,
        startDate,
        endDate,
        paidOnly,
      })
      if (response.error) throw new Error(response.error.message)
      return response.data
    },
  })
}

function reportTransactionsQueryOptions(
  orgSlug: string,
  startDate: string,
  endDate: string,
  paidOnly: boolean,
) {
  return queryOptions({
    queryKey: ["report-transactions", orgSlug, startDate, endDate, paidOnly],
    queryFn: async () => {
      const response = await listReportTransactions({
        organizationSlug: orgSlug,
        startDate,
        endDate,
        paidOnly,
      })
      if (response.error) throw new Error(response.error.message)
      return response.data
    },
  })
}

export const Route = createFileRoute("/_authenticated/$orgSlug/reports")({
  validateSearch: (search: Record<string, unknown>): TReportsSearch => {
    const defaults = getDefaultDates()
    return {
      startDate: (search.startDate as string) || defaults.startDate,
      endDate: (search.endDate as string) || defaults.endDate,
      paidOnly: search.paidOnly === undefined ? true : Boolean(search.paidOnly),
    }
  },
  loader: async ({ context, params }) => {
    const defaults = getDefaultDates()
    const orgSlug = params.orgSlug
    await Promise.all([
      context.queryClient.ensureQueryData(
        monthlyOverviewQueryOptions(
          orgSlug,
          defaults.startDate,
          defaults.endDate,
          true,
        ),
      ),
      context.queryClient.ensureQueryData(
        cashFlowQueryOptions(
          orgSlug,
          defaults.startDate,
          defaults.endDate,
          true,
        ),
      ),
      context.queryClient.ensureQueryData(
        categoryBreakdownQueryOptions(
          orgSlug,
          defaults.startDate,
          defaults.endDate,
          true,
        ),
      ),
      context.queryClient.ensureQueryData(
        byPersonQueryOptions(
          orgSlug,
          defaults.startDate,
          defaults.endDate,
          true,
        ),
      ),
      context.queryClient.ensureQueryData(
        reportTransactionsQueryOptions(
          orgSlug,
          defaults.startDate,
          defaults.endDate,
          true,
        ),
      ),
    ])
    return { title: "Relatórios", path: `/${params.orgSlug}/reports` }
  },
  component: ReportsPage,
})

function ReportsPage() {
  const { currentOrg } = useOrganization()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { breadcrumbs } = Route.useRouteContext()
  const path = Route.useLoaderData()

  const defaults = getDefaultDates()
  const orgSlug = currentOrg?.slug || ""

  const startDate = search.startDate || defaults.startDate
  const endDate = search.endDate || defaults.endDate
  const paidOnly = search.paidOnly ?? true

  useEffect(() => {
    breadcrumbs.setCurrentPath(path)
  }, [breadcrumbs, path])

  const { data: monthlyData } = useSuspenseQuery(
    monthlyOverviewQueryOptions(orgSlug, startDate, endDate, paidOnly),
  )

  const { data: cashFlowData } = useSuspenseQuery(
    cashFlowQueryOptions(orgSlug, startDate, endDate, paidOnly),
  )

  const { data: categoryData } = useSuspenseQuery(
    categoryBreakdownQueryOptions(orgSlug, startDate, endDate, paidOnly),
  )

  const { data: byPersonData } = useSuspenseQuery(
    byPersonQueryOptions(orgSlug, startDate, endDate, paidOnly),
  )

  const { data: transactionsData } = useSuspenseQuery(
    reportTransactionsQueryOptions(orgSlug, startDate, endDate, paidOnly),
  )

  const handleDateRangeChange = (
    newStartDate: string,
    newEndDate: string,
  ) => {
    navigate({
      search: (prev) => ({
        ...prev,
        startDate: newStartDate,
        endDate: newEndDate,
      }),
    })
  }

  const handlePaidOnlyChange = (value: boolean) => {
    navigate({ search: (prev) => ({ ...prev, paidOnly: value }) })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold md:text-3xl">Relatórios</h1>
          <p className="text-muted-foreground">
            Análise financeira por período
          </p>
        </div>
      </div>

      <ReportFilters
        startDate={startDate}
        endDate={endDate}
        paidOnly={paidOnly}
        onDateRangeChange={handleDateRangeChange}
        onPaidOnlyChange={handlePaidOnlyChange}
      />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Visão Mensal</CardTitle>
          </CardHeader>
          <CardContent>
            <MonthlyOverviewChart data={monthlyData?.data || []} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Fluxo de Caixa</CardTitle>
          </CardHeader>
          <CardContent>
            <CashFlowChart data={cashFlowData?.data || []} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Por Categoria</CardTitle>
          </CardHeader>
          <CardContent>
            <CategoryBreakdownChart
              data={categoryData?.data || { income: [], expense: [] }}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Por Pessoa</CardTitle>
          </CardHeader>
          <CardContent>
            <ByPersonChart
              data={byPersonData?.data || { income: [], expense: [] }}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Transações ({transactionsData?.transactions?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTransactionsTable
            transactions={transactionsData?.transactions || []}
          />
        </CardContent>
      </Card>
    </div>
  )
}
